import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Clock, Users, CheckCircle } from 'lucide-react';
import { getJobById } from '../data/jobs';
import { colors } from '../styles/theme';

const JobDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const job = getJobById(id || '');

  if (!job) {
    navigate('/jobs');
    return null;
  }

  const handleApply = () => {
    const subject = `Candidature ${job.title} - FRANCE STUDENT`;
    const body = `
Bonjour,

Je souhaite postuler au poste de ${job.title} au sein de FRANCE STUDENT.

Informations sur le poste :
- Département : ${job.department}
- Type : ${job.type}
- Localisation : ${job.location}
- Engagement : ${job.commitment}

Merci de compléter les informations suivantes :

Nom :
Prénom :
Formation actuelle :
Année d'études :
LinkedIn (optionnel) :
Portfolio/GitHub (optionnel) :

Motivation (expliquez en quelques lignes pourquoi vous souhaitez rejoindre FRANCE STUDENT) :


Expérience associative/professionnelle pertinente :


Disponibilité :


Cordialement,
    `.trim();

    window.location.href = `mailto:jobs@ffs.contact?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Link 
          to="/jobs" 
          className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour aux offres
        </Link>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-3xl font-bold mb-2" style={{ color: colors.primary }}>
                {job.title}
              </h1>
              <p className="text-xl text-gray-600">{job.department}</p>
            </div>
            <span 
              className="px-4 py-2 rounded-full text-sm font-medium"
              style={{
                backgroundColor: `${colors.primary}15`,
                color: colors.primary
              }}
            >
              {job.type}
            </span>
          </div>

          <div className="flex flex-wrap gap-6 mb-8 text-gray-600">
            <div className="flex items-center">
              <MapPin className="w-5 h-5 mr-2" />
              {job.location}
            </div>
            <div className="flex items-center">
              <Clock className="w-5 h-5 mr-2" />
              {job.commitment}
            </div>
            <div className="flex items-center">
              <Users className="w-5 h-5 mr-2" />
              {job.department}
            </div>
          </div>

          <div className="prose max-w-none mb-8">
            <p className="text-gray-600 text-lg">{job.description}</p>
          </div>

          <div className="space-y-8">
            <section>
              <h2 className="text-xl font-bold mb-4">Responsabilités</h2>
              <ul className="space-y-3">
                {job.responsibilities.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-3 mt-1 text-green-500" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-4">Prérequis</h2>
              <ul className="space-y-3">
                {job.requirements.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-3 mt-1 text-green-500" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-4">Compétences</h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-medium mb-3">Requises</h3>
                  <div className="flex flex-wrap gap-2">
                    {job.skills.required.map((skill, index) => (
                      <span 
                        key={index}
                        className="px-3 py-1 rounded-full text-sm"
                        style={{
                          backgroundColor: `${colors.primary}15`,
                          color: colors.primary
                        }}
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="font-medium mb-3">Appréciées</h3>
                  <div className="flex flex-wrap gap-2">
                    {job.skills.preferred.map((skill, index) => (
                      <span 
                        key={index}
                        className="px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-600"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <button 
              onClick={handleApply}
              className="w-full px-6 py-3 rounded-lg text-white transition-colors hover:bg-opacity-90"
              style={{ backgroundColor: colors.primary }}
            >
              Postuler maintenant
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetailPage;